import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Broadcast, Plus, Trash, WifiHigh, PencilSimple, CheckCircle, XCircle } from "@phosphor-icons/react";

const PLATFORMS = [
  ["youtube", "YouTube Live"], ["facebook", "Facebook Live"], ["x", "X (Twitter)"],
  ["linkedin", "LinkedIn Live"], ["instagram", "Instagram"], ["custom", "Custom RTMP"],
];

export default function BroadcastCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ platform: "youtube", name: "", rtmp_url: "", stream_key: "" });
  const [editing, setEditing] = useState(null);
  const [tests, setTests] = useState({});
  const [busy, setBusy] = useState(false);
  const [oauth, setOauth] = useState(null);
  const [premieres, setPremieres] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/broadcast/destinations"); setData(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    load();
    api.get("/broadcast/oauth/providers").then(({ data }) => setOauth(data.providers)).catch(() => {});
    api.get("/broadcast/premieres").then(({ data }) => setPremieres(data.premieres || [])).catch(() => {});
  }, []);
  const cancelPremiere = async (id) => {
    try { await api.delete(`/broadcast/premiere/${id}`); setPremieres((ps) => ps.filter((p) => p.id !== id)); toast.success("Premiere cancelled"); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected")) { toast.success(`${p.get("connected")} connected — a destination was added.`); }
    if (p.get("connect_error")) { toast.error(`Couldn't connect ${p.get("connect_error")}: ${p.get("detail") || "please try again."}`); }
    if (p.get("connected") || p.get("connect_error")) {
      window.history.replaceState({}, "", "/app/meet/broadcast");
    }
  }, []);

  const connect = async (provider) => {
    setAdding(true); setEditing(null);
    setForm((f) => ({ ...f, platform: provider, rtmp_url: data?.platform_hints?.[provider] || f.rtmp_url }));
    try {
      const { data: r } = await api.post(`/broadcast/oauth/${provider}/start`);
      if (r.auth_url) window.location.href = r.auth_url;
    } catch (e) {
      toast.info(apiError(e));
    }
  };

  const hint = (p) => data?.platform_hints?.[p] || "rtmp://";
  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.patch(`/broadcast/destinations/${editing}`, { name: form.name, rtmp_url: form.rtmp_url, stream_key: form.stream_key || undefined });
      else await api.post("/broadcast/destinations", form);
      toast.success(editing ? "Destination updated" : "Destination added");
      setAdding(false); setEditing(null); setForm({ platform: "youtube", name: "", rtmp_url: "", stream_key: "" });
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/broadcast/destinations/${id}`); await load(); } catch (e) { toast.error(apiError(e)); } };
  const test = async (id) => {
    setTests((t) => ({ ...t, [id]: { loading: true } }));
    try { const { data } = await api.post(`/broadcast/destinations/${id}/test`); setTests((t) => ({ ...t, [id]: data })); }
    catch (e) { setTests((t) => ({ ...t, [id]: { ok: false, detail: apiError(e) } })); }
  };
  const startEdit = (d) => { setEditing(d.id); setForm({ platform: d.platform, name: d.name, rtmp_url: d.rtmp_url, stream_key: "" }); setAdding(true); };

  if (!data) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!data.can_manage) {
    return (
      <div className="space-y-4" data-testid="broadcast-no-access">
        <button onClick={() => navigate("/app/meet")} className="flex items-center gap-1.5 text-sm text-muted-foreground"><ArrowLeft size={15} /> Back</button>
        <div className="rounded-2xl border border-border bg-card p-6"><h1 className="font-display text-2xl font-semibold">Live Broadcasting</h1><p className="mt-2 text-muted-foreground">Only a Host, Org admin or Broadcaster can manage streaming destinations.</p></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="broadcast-center">
      <button onClick={() => navigate("/app/meet")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back to meetings</button>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight"><Broadcast size={28} weight="duotone" /> Live Broadcasting</h1>
          <p className="mt-2 text-muted-foreground">Save your streaming destinations once, then go live from any meeting or webinar — to one platform or many at the same time.</p>
        </div>
        {!adding && <button data-testid="add-destination" onClick={() => { setAdding(true); setEditing(null); setForm({ platform: "youtube", name: "", rtmp_url: hint("youtube"), stream_key: "" }); }} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><Plus size={16} /> Add destination</button>}
      </div>

      {adding && (
        <div className="rounded-2xl border border-primary/40 bg-card p-5" data-testid="destination-form">
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/40 p-3">
            <span className="text-xs font-semibold text-muted-foreground">Quick connect:</span>
            <button data-testid="connect-youtube" onClick={() => connect("youtube")} className="rounded-full bg-[#FF0000] px-3 py-1.5 text-xs font-semibold text-white">Connect YouTube</button>
            <button data-testid="connect-facebook" onClick={() => connect("facebook")} className="rounded-full bg-[#1877F2] px-3 py-1.5 text-xs font-semibold text-white">Connect Facebook</button>
            <span className="text-[11px] text-muted-foreground">or paste your ingest URL &amp; key below.</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-muted-foreground">Platform
              <select data-testid="dest-platform" value={form.platform} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value, rtmp_url: f.rtmp_url || hint(e.target.value) }))} className="mt-1.5 w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary">
                {PLATFORMS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-muted-foreground">Label
              <input data-testid="dest-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Company YouTube" className="mt-1.5 w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary" />
            </label>
            <label className="text-xs font-semibold text-muted-foreground sm:col-span-2">RTMP / RTMPS ingest URL
              <input data-testid="dest-url" value={form.rtmp_url} onChange={(e) => setForm((f) => ({ ...f, rtmp_url: e.target.value }))} placeholder={hint(form.platform)} className="mt-1.5 w-full rounded-lg border border-input bg-card px-3 py-2.5 font-mono-metric text-sm outline-none focus:border-primary" />
            </label>
            <label className="text-xs font-semibold text-muted-foreground sm:col-span-2">Stream key {editing && <span className="font-normal text-muted-foreground">(leave blank to keep current)</span>}
              <input data-testid="dest-key" type="password" value={form.stream_key} onChange={(e) => setForm((f) => ({ ...f, stream_key: e.target.value }))} placeholder="Paste your secret stream key" className="mt-1.5 w-full rounded-lg border border-input bg-card px-3 py-2.5 font-mono-metric text-sm outline-none focus:border-primary" />
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="dest-save" disabled={busy || !form.rtmp_url} onClick={save} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">{editing ? "Save" : "Add destination"}</button>
            <button data-testid="dest-cancel" onClick={() => { setAdding(false); setEditing(null); }} className="rounded-full border border-border px-5 py-2 text-sm">Cancel</button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Keys are encrypted at rest and never shown again. Your stream is pushed to these destinations from your meeting.</p>
        </div>
      )}

      {premieres.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="premiere-center">
          <p className="mb-2 text-sm font-semibold">Upcoming premieres</p>
          <div className="space-y-2">
            {premieres.map((p) => <PremiereRow key={p.id} p={p} onCancel={cancelPremiere} />)}
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="destination-list">
        {data.destinations.length === 0 && !adding && <p className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">No destinations yet. Add YouTube, Facebook, X, LinkedIn, Instagram or any custom RTMP endpoint.</p>}
        {data.destinations.map((d) => {
          const t = tests[d.id];
          return (
            <div key={d.id} data-testid={`destination-${d.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{d.name} <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase">{d.platform}</span></p>
                <p className="truncate font-mono-metric text-xs text-muted-foreground">{d.rtmp_url} · key {d.key_masked || "—"}</p>
                {t && !t.loading && (t.ok
                  ? <p className="mt-1 flex items-center gap-1 text-xs text-emerald-500" data-testid={`test-ok-${d.id}`}><CheckCircle size={13} weight="fill" /> Reachable ({t.latency_ms}ms){!t.has_key && " · no key saved"}</p>
                  : <p className="mt-1 flex items-center gap-1 text-xs text-destructive" data-testid={`test-fail-${d.id}`}><XCircle size={13} weight="fill" /> {t.detail}</p>)}
              </div>
              <button data-testid={`test-${d.id}`} onClick={() => test(d.id)} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary">{t?.loading ? "Testing…" : <><WifiHigh size={14} /> Test</>}</button>
              <button data-testid={`edit-${d.id}`} onClick={() => startEdit(d)} className="rounded-full border border-border p-2 hover:border-primary"><PencilSimple size={14} /></button>
              <button data-testid={`delete-${d.id}`} onClick={() => del(d.id)} className="rounded-full border border-border p-2 text-destructive hover:border-destructive"><Trash size={14} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function PremiereRow({ p, onCancel }) {
  const [open, setOpen] = useState(false);
  const [regs, setRegs] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [emails, setEmails] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/broadcast/premiere/${p.watch_code}/share`;
  const view = async () => {
    if (!open && regs === null) {
      try { const { data } = await api.get(`/broadcast/premiere/${p.id}/registrations`); setRegs(data); }
      catch (e) { toast.error(apiError(e)); return; }
    }
    setOpen((o) => !o);
  };
  const exportCsv = async () => {
    try {
      const res = await api.get(`/broadcast/premiere/${p.id}/registrations.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `premiere-rsvps.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendInvites = async () => {
    const list = emails.split(/[\s,;]+/).filter(Boolean);
    if (!list.length) { toast.error("Add at least one email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/broadcast/premiere/${p.id}/invite`, { emails: list, message: msg });
      toast.success(`Invited ${data.sent} of ${data.invited}`);
      setEmails(""); setMsg(""); setInviting(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const importCsv = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const found = String(reader.result || "").match(/[\w.+-]+@[\w-]+\.[\w.-]+/g) || [];
      const merged = Array.from(new Set([...emails.split(/[\s,;]+/).filter(Boolean), ...found]));
      if (!found.length) { toast.error("No email addresses found in that file"); return; }
      setEmails(merged.join("\n"));
      toast.success(`Loaded ${found.length} email${found.length === 1 ? "" : "s"} from CSV`);
    };
    reader.readAsText(file);
  };
  const btn = "rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary";
  return (
    <div data-testid={`bc-premiere-${p.id}`} className="rounded-xl border border-border/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{p.title} {p.status === "ready" && <span className="ml-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-600">ready</span>}</p>
          <p className="text-xs text-muted-foreground">{new Date(p.scheduled_at).toLocaleString()} · room {p.room_code} · {p.registrations || 0} RSVP</p>
        </div>
        <button data-testid={`bc-premiere-rsvps-${p.id}`} onClick={view} className={btn}>{open ? "Hide" : "View"} RSVPs</button>
        <button data-testid={`bc-premiere-invite-${p.id}`} onClick={() => setInviting((v) => !v)} className={btn}>Invite</button>
        <button data-testid={`bc-premiere-csv-${p.id}`} onClick={exportCsv} className={btn}>Export CSV</button>
        <a data-testid={`bc-premiere-promo-${p.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/broadcast/premiere/${p.watch_code}/promo.png`} target="_blank" rel="noreferrer" className={btn}>Promo image</a>
        <button data-testid={`bc-premiere-copy-${p.id}`} onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Share link copied"); }} className={btn}>Copy link</button>
        <button data-testid={`bc-premiere-cancel-${p.id}`} onClick={() => onCancel(p.id)} className="rounded-full border border-border p-2 text-destructive hover:border-destructive"><Trash size={14} /></button>
      </div>
      {inviting && (
        <div className="mt-3 border-t border-border/50 pt-3" data-testid={`bc-invite-${p.id}`}>
          <textarea data-testid={`invite-emails-${p.id}`} value={emails} onChange={(e) => setEmails(e.target.value)} rows={2}
            placeholder="Emails, separated by commas or new lines" className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <input data-testid={`invite-msg-${p.id}`} value={msg} onChange={(e) => setMsg(e.target.value)}
            placeholder="Optional personal note" className="mt-2 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button data-testid={`invite-send-${p.id}`} disabled={busy} onClick={sendInvites}
              className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {busy ? "Sending…" : "Send invites (with promo image)"}
            </button>
            <label data-testid={`invite-csv-label-${p.id}`} className="cursor-pointer rounded-full border border-border px-4 py-2 text-xs font-semibold hover:border-primary">
              Import CSV
              <input data-testid={`invite-csv-${p.id}`} type="file" accept=".csv,text/csv,text/plain" onChange={importCsv} className="hidden" />
            </label>
          </div>
        </div>
      )}
      {open && regs && (
        <div className="mt-3 border-t border-border/50 pt-3" data-testid={`bc-rsvps-${p.id}`}>
          {regs.count === 0 ? <p className="text-xs text-muted-foreground">No RSVPs yet — share the watch link to collect sign-ups.</p> : (
            <ul className="space-y-1 text-xs">
              {regs.registrations.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <span className="font-medium">{r.name || "Guest"}</span>
                  <span className="text-muted-foreground">{r.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
